import { filterAccountsByPlatformType, splitAccounts } from "utils/accounts";
import { getEntityMap } from "./selectorUtils";
import { createSelector } from "@reduxjs/toolkit";
import { getUser } from "./commonSelectors";
import { AccountGroupType } from "../features/account-groups-form/accountsGroups.model";
import { ReduxRootState } from "../types/redux-store";
import { AccountType } from "../shared/types/accounts";
import {
  getAvailableSocialSetSlotsFromUsed,
  getUsedSocialSetSlots,
  SocialSetSlotKey,
} from "../utils/social-set";

const ACCOUNT_ENTITY = "accounts";

const emptyAccountsState = {
  byId: {},
  allIds: [],
  selectedIds: [],
  counts: {
    fbGroupCount: 0,
    fbPageCount: 0,
    gmbCount: 0,
    igAllowed: 0,
    igCount: 0,
    igFollowers: 0,
    liCount: 0,
    otherAllowed: 0,
    otherCount: 0,
    piCount: 0,
    twCount: 0,
    usesSkedLink: false,
    ytCount: 0,
    notificationCount: 0,
    notificationAllowed: 0,
    maxAllowed: 0,
    totalCount: 0,
    pricingVersion: "",
  },
  withToken: [],
};

const getAccountsState = (state: ReduxRootState) => {
  const accountsState = state.entities?.[ACCOUNT_ENTITY];
  const byId = accountsState?.byId ?? {};

  return {
    ...emptyAccountsState,
    ...accountsState,
    byId,
    allIds: accountsState?.allIds ?? Object.keys(byId),
  };
};

export const getAccountsById = (state: ReduxRootState) =>
  getAccountsState(state).byId;
export const getAccountIds = (state: ReduxRootState) =>
  getAccountsState(state).allIds;
export const getSelectedAccountIds = (state: ReduxRootState) =>
  getAccountsState(state).selectedIds;

export const getSelectedAccounts = createSelector(
  getAccountIds,
  getAccountsById,
  getSelectedAccountIds,
  (allIds, byId, selectedIds) => {
    return getEntityMap(allIds, byId, selectedIds);
  }
);

export const getAccounts = createSelector(
  getAccountIds,
  getAccountsById,
  (allIds, byId) =>
    allIds
      .map((accountId) => byId[accountId])
      .filter((account): account is AccountType => Boolean(account))
);

export const getAccountsGroups = createSelector(
  (state: ReduxRootState) => state.entities?.accountGroups?.entities ?? {},
  (entities) => {
    if ("byId" in entities && "allIds" in entities) {
      return entities.allIds
        .map((id) => entities.byId[id])
        .filter(Boolean) as AccountGroupType[];
    }

    return Object.values(entities) as AccountGroupType[];
  }
);

export const getAccountsByGroup = createSelector(
  getAccounts,
  getAccountsGroups,
  getUser,
  (accounts: AccountType[], accountGroups: AccountGroupType[], user) => {
    // NOTE: (Andrey) only admin can see empty group, because not possible give access to empty group for collaborators
    const isAdmin = user && user.role === "admin";
    const { groupedAccounts, otherAccounts } = splitAccounts(
      accounts,
      isAdmin ? accountGroups : []
    );

    return {
      groupedAccounts,
      otherAccounts,
      allAccounts: accounts,
      accountGroups,
    };
  }
);

export const getApprovalsAccountsByGroup = getAccountsByGroup;

export const getAccountsCounts = (state: ReduxRootState) =>
  getAccountsState(state).counts;

export const getAccountsForPlatform = (
  state: ReduxRootState,
  platform: string
) => {
  const allAccounts = getAccounts(state);

  return allAccounts.filter(filterAccountsByPlatformType(platform));
};

export const getAccount = (state: ReduxRootState, accountId: string) => {
  const account = getAccountsById(state)[accountId];
  if (!account) {
    return null;
  }
  return account;
};

export const getIGTokenAccounts = (state: ReduxRootState) =>
  getAccountsState(state).withToken;

/**
 * `resultEqualityCheck` compares Set contents so `useSelector` subscribers
 * get a stable reference when the occupied slots haven't actually changed.
 * Without it, every upstream `getAccounts` change (e.g. a name edit)
 * produces a fresh `Set` and forces every consumer to re-render. The
 * downstream `getSocialSetAvailability` chain inherits this stability
 * automatically — when used-slots is unchanged, the chain short-circuits.
 */
const slotSetEqual = (
  a: Set<SocialSetSlotKey>,
  b: Set<SocialSetSlotKey>
): boolean => {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const slot of a) if (!b.has(slot)) return false;
  return true;
};

/** Set of slot keys currently occupied by the user's accounts. */
export const getSocialSetUsedSlots = createSelector(
  getAccounts,
  (accounts): Set<SocialSetSlotKey> => getUsedSocialSetSlots(accounts),
  { memoizeOptions: { resultEqualityCheck: slotSetEqual } }
);

/**
 * Returns the set of social-set slot keys still available based on the
 * user's connected accounts. Used by the connect dialog and counters when
 * the user has the "social-set-only" entitlement enabled.
 *
 * Chained off `getSocialSetUsedSlots` (not `getAccounts` directly) so a
 * component subscribing to both selectors only traverses the accounts list
 * once per accounts change.
 */
export const getSocialSetAvailability = createSelector(
  getSocialSetUsedSlots,
  getAvailableSocialSetSlotsFromUsed
);

/** Type for accounts fetch status */
type AccountsFetchStatus = "idle" | "loading" | "succeeded" | "failed";

/**
 * Returns the current fetch status of accounts.
 * @see reducers/entities/accountsReducer.js for the fetchStatus field.
 */
export const getAccountsFetchStatus = (
  state: ReduxRootState
): AccountsFetchStatus => {
  return (
    (state.entities?.accounts?.fetchStatus as AccountsFetchStatus) ?? "idle"
  );
};

/**
 * Returns true if accounts are currently being fetched.
 * Uses the dedicated fetchStatus field in the accounts reducer.
 */
export const getAccountsLoading = (state: ReduxRootState): boolean => {
  return getAccountsFetchStatus(state) === "loading";
};

/**
 * Returns true when accounts have been successfully loaded and are ready to use.
 * Checks that accounts fetch completed with SUCCESS status (not FAILURE).
 *
 * Note: A user with zero accounts is valid - we check if the fetch succeeded,
 * not if accounts exist.
 *
 * This distinguishes between:
 * - 'succeeded': Accounts fetch completed successfully, ready to use
 * - 'failed': Accounts fetch failed, should show error, NOT ready
 * - 'loading': Still fetching, NOT ready
 * - 'idle': Fetch never started, NOT ready
 */
export const getAccountsReady = (state: ReduxRootState): boolean => {
  return getAccountsFetchStatus(state) === "succeeded";
};

/**
 * Returns true if accounts fetch failed.
 * Useful for showing error states in UI.
 */
export const getAccountsFetchFailed = (state: ReduxRootState): boolean => {
  return getAccountsFetchStatus(state) === "failed";
};
